/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/set-state-in-effect */
// Lint debt: 33 pre-existing warnings from before max-warnings=0 was graduated
// (file last touched in Session 13). Structural `useEffect(() => { ...; fetch(); ... })`
// and bare-promise `fetch()` call patterns across ~25 sites. Silenced at file level
// to ship §1.37 without a 25-line inline-silence commit or an out-of-scope
// lint-debt-sweep commit. Cleanup filed post-launch; this session's new code at
// line 235 uses `void fetch()` to not add to the debt.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { canonicalMoney, sumMoney, formatMoney } from '../../lib/money';
import { createInvoice } from '../../lib/invoiceMutations';
import { recordExpense } from '../../lib/expenseMutations';
import { createProjectQuote, updateProjectQuote, respondProjectQuote, setQuoteLineItems, setPaymentSchedule } from '../../lib/quoteMutations';
import { deriveOnSignatureDeposit } from '../../lib/paymentSchedule';
import { callMoneyRpc } from '../../lib/moneyRpc';
import type { ProjectPhase, ProjectTask, ProjectGate, ProjectQuote, QuoteLineItem, ProjectPhoto, ProjectExpense, ProjectPayment, ProjectAudit } from '../../types';
import { logActivity, logAudit } from './hooks';
import { hostedPageUrl } from '../../lib/hostedPageUrl';
import { useAuthStore } from '../../stores/authStore';

// ── Phases ──
export function usePhases(projectId: string | null, businessId: string) {
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setPhases([]); return; }
    const { data } = await supabase.from('project_phases').select('*').eq('project_id', projectId).eq('removed', false).order('phase_order');
    if (mountedRef.current) setPhases((data ?? []) as ProjectPhase[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const createPhase = useCallback(async (name: string, order: number, uid: string) => {
    if (!projectId) return false;
    const { error: e } = await supabase.from('project_phases').insert({ project_id: projectId, name, phase_order: order, sort_order: order, status: 'pending', progress: 0 });
    if (e) return false;
    await logActivity(businessId, 'phase_created', `Phase added: ${name}`, projectId, uid);
    await logAudit(projectId, 'phase_created', { name }, null, null, uid);
    fetch(); return true;
  }, [projectId, businessId, fetch]);

  const updatePhase = useCallback(async (phaseId: string, data: Partial<ProjectPhase>, uid: string) => {
    if (!projectId) return false;
    const { error: e } = await supabase.from('project_phases').update(data).eq('id', phaseId);
    if (e) return false;
    await logAudit(projectId, 'phase_updated', data as Record<string, unknown>, null, null, uid);
    fetch(); return true;
  }, [projectId, fetch]);

  return { phases, createPhase, updatePhase, refresh: fetch };
}

// ── Tasks ──
export function useTasks(projectId: string | null, _businessId: string) {
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setTasks([]); return; }
    const { data } = await supabase.from('project_tasks').select('*').eq('project_id', projectId).order('sort_order');
    if (mountedRef.current) setTasks((data ?? []) as ProjectTask[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const createTask = useCallback(async (phaseId: string, name: string, order: number, uid: string) => {
    if (!projectId) return false;
    const { error: e } = await supabase.from('project_tasks').insert({ project_id: projectId, phase_id: phaseId, name, sort_order: order, status: 'not_started', is_required: true });
    if (e) return false;
    await logAudit(projectId, 'task_created', { name, phase_id: phaseId }, null, null, uid);
    void fetch(); return true;
  }, [projectId, fetch]);

  const toggleTask = useCallback(async (taskId: string, currentStatus: string, uid: string) => {
    if (!projectId) return false;
    const flow = ['not_started', 'in_progress', 'completed'];
    const nextIdx = (flow.indexOf(currentStatus) + 1) % flow.length;
    const next = flow[nextIdx];
    const updates: Record<string, unknown> = { status: next };
    if (next === 'completed') { updates.completed_at = new Date().toISOString(); updates.completed_by = uid; }
    else { updates.completed_at = null; updates.completed_by = null; }
    const { error: e } = await supabase.from('project_tasks').update(updates).eq('id', taskId);
    if (e) return false;
    await logAudit(projectId, 'task_status', { task_id: taskId }, currentStatus, next, uid);
    fetch(); return true;
  }, [projectId, fetch]);

  const assignTask = useCallback(async (taskId: string, assigneeId: string | null, _uid: string) => {
    if (!projectId) return false;
    const { error: e } = await supabase.from('project_tasks').update({ assigned_to: assigneeId }).eq('id', taskId);
    if (e) return false;
    fetch(); return true;
  }, [projectId, fetch]);

  return { tasks, createTask, toggleTask, assignTask, refresh: fetch };
}

// ── Gates ──
export function useGates(projectId: string | null, businessId: string) {
  const [gates, setGates] = useState<ProjectGate[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setGates([]); return; }
    const { data } = await supabase.from('project_gates').select('*').eq('project_id', projectId).order('sort_order');
    if (mountedRef.current) setGates((data ?? []) as ProjectGate[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const createGate = useCallback(async (name: string, gateType: string, afterPhaseId: string | null, order: number, uid: string) => {
    if (!projectId) return false;
    const { error: e } = await supabase.from('project_gates').insert({ project_id: projectId, name, gate_type: gateType, after_phase_id: afterPhaseId, sort_order: order, status: 'pending', is_required: true });
    if (e) return false;
    await logActivity(businessId, 'gate_created', `Gate added: ${name}`, projectId, uid);
    await logAudit(projectId, 'gate_created', { name, gate_type: gateType }, null, null, uid);
    fetch(); return true;
  }, [projectId, businessId, fetch]);

  const resolveGate = useCallback(async (gateId: string, action: 'resolved' | 'waived', note: string | null, uid: string) => {
    if (!projectId) return false;
    const updates: Record<string, unknown> = { status: action, status_changed_at: new Date().toISOString(), status_changed_by: uid };
    if (action === 'waived') updates.waive_reason = note;
    else updates.resolution_note = note;
    const { error: e } = await supabase.from('project_gates').update(updates).eq('id', gateId);
    if (e) return false;
    await logActivity(businessId, 'gate_resolved', `Gate ${action}`, projectId, uid);
    await logAudit(projectId, `gate_${action}`, { gate_id: gateId, note }, 'pending', action, uid);
    fetch(); return true;
  }, [projectId, businessId, fetch]);

  return { gates, createGate, resolveGate, refresh: fetch };
}

// ── Quotes + Line Items ─���
export function useQuotes(projectId: string | null, businessId: string) {
  const [quotes, setQuotes] = useState<ProjectQuote[]>([]);
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setQuotes([]); setLineItems([]); return; }
    // Columns enumerated per GR-15 (G-U3 / §T.117) to match ProjectQuote + QuoteLineItem interfaces.
    const qRes = await supabase.from('quotes')
      .select('id, project_id, business_id, client_id, client_name, client_phone, status, subtotal, tax_rate, tax_amount, total, sent_at, valid_days, expires_at, approved_at, declined_at, client_notes, share_token, created_by, created_at, updated_at, followup_sent_at, payment_schedule_items(id, sort_order, description, amount_type, amount, trigger_type, trigger_date, status, invoice_id)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (mountedRef.current) { setQuotes((qRes.data ?? []) as ProjectQuote[]); }
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const fetchLineItems = useCallback(async (quoteId: string) => {
    const { data } = await supabase.from('quote_line_items')
      .select('id, quote_id, line_type, description, quantity, unit, unit_price, subtotal, phase_id, sort_order, created_at')
      .eq('quote_id', quoteId).order('sort_order');
    if (mountedRef.current) setLineItems((data ?? []) as QuoteLineItem[]);
  }, []);

  // Q2 Phase 2a: routed through `create_quote` + `set_quote_line_items`.
  // The substrate RPC requires non-empty line_items at create time; admin's UX
  // is "create empty draft, operator adds items in detail view," so we send a
  // single placeholder + immediately clear it (set_quote_line_items accepts an
  // empty array and recomputes totals to 0). Net result: empty draft quote with
  // a server-generated share_token and tax_rate from business_financial_settings.
  // Auto-fixes the legacy `tax_rate: 13` (percent shape) bug — RPC reads the
  // hst_rate fraction when p_tax_rate is null.
  const createQuote = useCallback(async (uid: string): Promise<string | null> => {
    if (!projectId) return null;
    const created = await createProjectQuote({
      projectId,
      lineItems: [{ description: '__placeholder__', quantity: 1, unit_price: 0 }],
      taxRate: null,
    });
    if (!created.success) return null;
    const quoteId = created.data.quote_id;
    await setQuoteLineItems(quoteId, []);
    await logActivity(businessId, 'quote_created', 'Quote created', projectId, uid);
    await logAudit(projectId, 'quote_created', { quote_id: quoteId }, null, 'draft', uid);
    fetch(); return quoteId;
  }, [projectId, businessId, fetch]);

  // Line-item add / remove / edit all route through `set_quote_line_items`
  // (replace-all). Each fetches the current full list, transforms it locally,
  // and sends the full list back. RPC atomically deletes existing rows + bulk-
  // inserts + recomputes subtotal/tax/total/deposit/progress/final amounts
  // using the quote's stored tax_rate and percentages — retiring the prior
  // `recalcQuote` helper.
  async function fetchCurrentLineItems(quoteId: string): Promise<QuoteLineItem[]> {
    const { data } = await supabase
      .from('quote_line_items')
      .select('id, quote_id, line_type, description, quantity, unit, unit_price, subtotal, phase_id, sort_order, created_at')
      .eq('quote_id', quoteId)
      .order('sort_order');
    return (data ?? []) as QuoteLineItem[];
  }

  const addLineItem = useCallback(async (quoteId: string, item: Partial<QuoteLineItem>, uid: string) => {
    const existing = await fetchCurrentLineItems(quoteId);
    const next = [...existing, { ...item, sort_order: item.sort_order ?? existing.length }];
    const r = await setQuoteLineItems(quoteId, next);
    if (!r.success) return false;
    fetchLineItems(quoteId);
    await logAudit(projectId!, 'line_item_added', { description: item.description }, null, null, uid);
    return true;
  }, [projectId, fetchLineItems]);

  const removeLineItem = useCallback(async (itemId: string, quoteId: string, uid: string) => {
    const existing = await fetchCurrentLineItems(quoteId);
    const next = existing.filter((li) => li.id !== itemId);
    const r = await setQuoteLineItems(quoteId, next);
    if (!r.success) return false;
    fetchLineItems(quoteId);
    await logAudit(projectId!, 'line_item_removed', { item_id: itemId }, null, null, uid);
    return true;
  }, [projectId, fetchLineItems]);

  const updateLineItem = useCallback(async (itemId: string, data: Partial<QuoteLineItem>, quoteId: string) => {
    const existing = await fetchCurrentLineItems(quoteId);
    const next = existing.map((li) => (li.id === itemId ? { ...li, ...data } : li));
    const r = await setQuoteLineItems(quoteId, next);
    if (!r.success) return false;
    fetchLineItems(quoteId);
    return true;
  }, [fetchLineItems]);

  const sendQuote = useCallback(async (quoteId: string, uid: string) => {
    if (!projectId) return false;
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) return false;

    // Q2 Phase 2a: share_token always present — `create_quote`
    // generates it server-side. Legacy NULL-share_token rows verified absent at
    // refactor time; the prior defensive backfill block was dead code.
    const shareToken = quote.share_token;
    if (!shareToken) return false;

    // Hydrate client email via project.client_id → clients.email.
    const { data: proj, error: projErr } = await supabase
      .from('projects')
      .select('client_id')
      .eq('id', projectId)
      .maybeSingle();
    if (projErr || !proj) return false;

    let clientEmail: string | null = null;
    if (proj.client_id) {
      const { data: cl } = await supabase
        .from('clients')
        .select('email')
        .eq('id', proj.client_id)
        .maybeSingle();
      clientEmail = cl?.email?.trim() || null;
    }

    // Phase B.3.4-X: status flip routed through `update_project_quote`. RPC
    // stamps sent_at + expires_at server-side. The "caller owns status flip
    // before delivery, edge function owns delivery" contract (§1.37 Phase 3)
    // is preserved — RPC just owns the status mechanics now.
    const flip = await updateProjectQuote(quoteId, { status: 'sent' });
    if (!flip.success) return false;

    // Hosted quote page (crewbit.app/{slug}/q/{token}); the admin /quote/:token route was retired at §T.115.
    const businessSlug = useAuthStore.getState().business?.slug;
    if (!businessSlug) return false;
    const portalUrl = hostedPageUrl(businessSlug, 'q', shareToken);

    let delivery: 'email' | 'clipboard' = 'clipboard';
    if (clientEmail) {
      const { error: invokeErr } = await supabase.functions.invoke('send-quote-email', {
        body: {
          quote_id: quoteId,
          share_token: shareToken,
          client_email: clientEmail,
          business_id: businessId,
        },
      });
      if (!invokeErr) delivery = 'email';
    }

    if (delivery === 'clipboard') {
      try { await navigator.clipboard.writeText(portalUrl); } catch { /* clipboard unavailable */ }
    }

    const desc = delivery === 'email'
      ? `Quote sent to ${clientEmail}`
      : 'Quote marked sent — portal link copied to clipboard';
    await logActivity(businessId, 'quote_sent', desc, projectId, uid);
    await logAudit(projectId, 'quote_sent', { quote_id: quoteId, delivery }, 'draft', 'sent', uid);
    void fetch();
    return true;
  }, [projectId, businessId, quotes, fetch]);

  // Phase B.3.4-X: approve / decline routed through `respond_to_project_quote`.
  // RPC stamps approved_at / declined_at + activity_log atomically and is
  // idempotent (re-approving an approved quote returns already_responded:true
  // without error). Requires current status sent or viewed — drafts get
  // QUOTE_NOT_RESPONDABLE, which is the correct behavior.
  const updateQuoteStatus = useCallback(async (quoteId: string, status: 'approved' | 'declined', uid: string) => {
    const r = await respondProjectQuote(quoteId, status);
    if (!r.success) return false;
    await logActivity(businessId, `quote_${status}`, `Quote ${status}`, projectId!, uid);
    await logAudit(projectId!, `quote_${status}`, { quote_id: quoteId }, 'sent', status, uid);

    // Auto-create deposit invoice when quote approved (best-effort).
    // Phase B.3.2: routed through canonical RPC. Server resolves identity,
    // invoice_number, business snapshot, totals, project linkage, activity_log.
    // Idempotency key scoped to (quote, deposit) so re-approval of the same
    // quote doesn't double-bill the deposit.
    if (status === 'approved' && projectId) {
      const quote = quotes.find(q => q.id === quoteId);
      // Q2 Phase 2a: deposit derived from on_signature payment_schedule_items row,
      // mirroring server-side approve_quote_by_token cascade derivation.
      const derivedDeposit = quote
        ? deriveOnSignatureDeposit(quote.payment_schedule_items ?? [], quote.total)
        : 0;
      if (quote && derivedDeposit > 0) {
        try {
          const { data: proj } = await supabase.from('projects').select('client_name, client_id').eq('id', projectId).maybeSingle();
          if (proj) {
            const depositItem = [{ description: 'Deposit — project quote approved', quantity: 1, unit_price: derivedDeposit, amount: derivedDeposit }];
            const dueDate = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];

            // quote.tax_rate is fraction (e.g. 0.13) — passed through to the
            // invoice RPC unchanged. derivedDeposit is the resolved amount per
            // the on_signature schedule row (post-tax base via quote.total).
            const result = await createInvoice({
              businessId,
              clientId: proj.client_id ?? null,
              lineItems: depositItem,
              dueDate,
              notes: 'Auto-generated deposit invoice for project',
              taxRate: quote.tax_rate,
              idempotencyKey: `project-deposit-${quoteId}`,
              clientName: proj.client_name ?? 'Client',
              linkedProjectId: projectId,
            });
            if (result.success) {
              await logActivity(businessId, 'invoice_created', `Deposit invoice auto-created — ${formatMoney(canonicalMoney(derivedDeposit))}`, projectId, uid);
            }
          }
        } catch { /* non-critical — quote approval succeeded, invoice is bonus */ }
      }
    }

    fetch(); return true;
  }, [projectId, businessId, quotes, fetch]);

  // Q2 Phase 2a: payment schedule routed through dedicated `set_payment_schedule_items`
  // RPC (replace-all). Server validates the canonical 3-row shape via the shared
  // helper, writes payment_schedule_items rows, no longer touches quotes.* columns.
  const updatePaymentSchedule = useCallback(async (quoteId: string, deposit: number, progress: number, final_pct: number) => {
    const r = await setPaymentSchedule(quoteId, {
      depositPercentage: deposit,
      progressPercentage: progress,
      finalPercentage: final_pct,
    });
    if (!r.success) return false;
    fetch(); return true;
  }, [fetch]);

  return { quotes, lineItems, fetchLineItems, createQuote, addLineItem, removeLineItem, updateLineItem, sendQuote, updateQuoteStatus, updatePaymentSchedule, refresh: fetch };
}

// ── Photos ─��
export function usePhotos(projectId: string | null) {
  const [photos, setPhotos] = useState<ProjectPhoto[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setPhotos([]); return; }
    const { data } = await supabase.from('project_photos').select('*').eq('project_id', projectId).order('created_at', { ascending: false });
    if (mountedRef.current) setPhotos((data ?? []) as ProjectPhoto[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const grouped = useMemo(() => {
    const map: Record<string, ProjectPhoto[]> = { before: [], during: [], after: [] };
    for (const p of photos) { (map[p.tag] ?? (map[p.tag] = [])).push(p); }
    return map;
  }, [photos]);

  const upload = useCallback(async (file: File, tag: 'before' | 'during' | 'after', userId: string) => {
    if (!projectId) return false;
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `project-photos/${projectId}/${tag}/${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('project-photos').upload(path, file, { contentType: file.type });
      if (uploadErr) return false;
      const { data: { publicUrl } } = supabase.storage.from('project-photos').getPublicUrl(path);
      const { error: insertErr } = await supabase.from('project_photos').insert({
        project_id: projectId,
        storage_path: publicUrl,
        tag,
        caption: null,
        uploaded_by: userId,
      });
      if (insertErr) return false;
      fetch();
      return true;
    } catch { return false; }
  }, [projectId, fetch]);

  return { photos, grouped, refresh: fetch, upload };
}

// ── Expenses ──
export function useExpenses(projectId: string | null, businessId: string) {
  const [expenses, setExpenses] = useState<ProjectExpense[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setExpenses([]); return; }
    // M-A4-027: project_expenses table dropped; reads `expenses` filtered by
    // project_id. Column aliases bridge schema diff: expense_date↔date,
    // receipt_photo_url↔receipt_url. ProjectExpense type shape preserved.
    const { data } = await supabase.from('expenses').select('id, project_id, phase_id, category, description, amount, logged_by, created_at, expense_date:date, receipt_photo_url:receipt_url').eq('project_id', projectId).order('date', { ascending: false });
    if (mountedRef.current) setExpenses((data ?? []) as ProjectExpense[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const total = useMemo(() => Number(sumMoney(expenses.map(e => canonicalMoney(e.amount)))), [expenses]);

  const logExpense = useCallback(async (data: Partial<ProjectExpense>, uid: string) => {
    if (!projectId || !data.amount || !data.category) return false;
    // Phase B.3.3: routed through canonical record_expense (M-A4-016 atomic
    // expenses + payment_ledger + activity_log; M-A4-020 added p_phase_id).
    // Drops the dual-write: project_expenses + expenses + activity_log.
    const result = await recordExpense({
      businessId,
      amount: data.amount,
      category: data.category,
      description: data.description ?? null,
      receiptUrl: data.receipt_photo_url ?? null,
      expenseDate: data.expense_date ?? null,
      idempotencyKey: crypto.randomUUID(),
      linkedProjectId: projectId,
      phaseId: data.phase_id ?? null,
    });
    if (!result.success) return false;
    await logAudit(projectId, 'expense_logged', { amount: data.amount, description: data.description }, null, null, uid);
    fetch(); return true;
  }, [projectId, businessId, fetch]);

  return { expenses, total, logExpense, refresh: fetch };
}

// ── Payments ──
export function usePayments(projectId: string | null, businessId: string) {
  const [payments, setPayments] = useState<ProjectPayment[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setPayments([]); return; }
    const { data } = await supabase.from('project_payments').select('*').eq('project_id', projectId).order('created_at', { ascending: false });
    if (mountedRef.current) setPayments((data ?? []) as ProjectPayment[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const totalPaid = useMemo(() => Number(sumMoney(payments.map(p => canonicalMoney(p.amount)))), [payments]);

  const recordPayment = useCallback(async (type: string, amount: number, method: string | null, uid: string) => {
    if (!projectId) return false;
    // record_project_payment (M-A4-019) — manual operator-driven path. With
    // p_invoice_id=NULL, RPC defaults status='received' + received_date=today.
    // Auto-fixes the pre-existing detailHooks bug where the legacy direct
    // insert wrote nonexistent columns ('type' should be 'payment_type',
    // 'paid_at' should be 'received_date'); RPC uses correct column names.
    const result = await callMoneyRpc<{
      project_payment_id: string;
      status: string;
      received_date: string | null;
      already_created: boolean;
    }>('record_project_payment', {
      p_project_id: projectId,
      p_invoice_id: null,
      p_payment_type: type,
      p_amount: amount,
      p_payment_method: method,
    });
    if (!result.success) return false;
    await logActivity(businessId, 'payment_recorded', `Payment: ${formatMoney(amount)} (${type})`, projectId, uid);
    await logAudit(projectId, 'payment_recorded', { type, amount, method }, null, null, uid);
    fetch(); return true;
  }, [projectId, businessId, fetch]);

  return { payments, totalPaid, recordPayment, refresh: fetch };
}

// ���─ Audit trail ──
export function useAuditTrail(projectId: string | null) {
  const [entries, setEntries] = useState<ProjectAudit[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!projectId) { setEntries([]); return; }
    const { data } = await supabase.from('project_audit').select('*').eq('project_id', projectId).order('performed_at', { ascending: false }).limit(50);
    if (mountedRef.current) setEntries((data ?? []) as ProjectAudit[]);
  }, [projectId]);
  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  return { entries, refresh: fetch };
}
